/**
 * Écran de connexion.
 *
 * ⚠️ La cérémonie WebAuthn part **au clic**, jamais au montage : pas d'appel
 * automatique, pas d'interface conditionnelle (`useBrowserAutofill`). Une invite
 * Face ID qui surgit à l'ouverture de la page serait à la fois hors spécification
 * et déroutante.
 *
 * La clé de secours est reléguée derrière un dépliant : c'est le chemin
 * exceptionnel (specs §9.2), il ne doit pas concurrencer visuellement la passkey.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { loginWithPasskey, loginWithRecoveryKey } from '../../api/auth';
import { ApiError } from '../../api/client';
import { Button, ErrorNote, Field, Hint } from '../../components/ui/primitives';
import { AuthShell } from './parts';

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) {
    // `NotAllowedError` couvre aussi bien l'annulation que l'expiration du
    // délai : le message natif n'apprend rien à l'utilisateur.
    if (error.name === 'NotAllowedError') return 'Connexion annulée.';
    return error.message;
  }
  return 'Échec inattendu.';
}

export function LoginScreen() {
  const queryClient = useQueryClient();
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryKey, setRecoveryKey] = useState('');

  const refreshStatus = () => queryClient.invalidateQueries({ queryKey: ['auth'] });

  const passkey = useMutation({
    mutationFn: loginWithPasskey,
    onSuccess: refreshStatus,
  });

  const recovery = useMutation({
    mutationFn: () => loginWithRecoveryKey(recoveryKey.trim()),
    onSuccess: async () => {
      setRecoveryKey('');
      await refreshStatus();
    },
  });

  return (
    <AuthShell title="Nutri">
      <Hint>Application personnelle. L’accès se fait par passkey.</Hint>

      {passkey.isError && <ErrorNote>{describe(passkey.error)}</ErrorNote>}

      <Button
        disabled={passkey.isPending}
        onClick={() => {
          passkey.mutate();
        }}
      >
        {passkey.isPending ? 'Connexion…' : 'Se connecter'}
      </Button>

      {!showRecovery && (
        <Button
          variant="quiet"
          onClick={() => {
            setShowRecovery(true);
          }}
        >
          Utiliser la clé de secours
        </Button>
      )}

      {showRecovery && (
        <>
          <Hint>
            À n’utiliser que si toutes les passkeys sont perdues. Toutes les sessions ouvertes
            seront fermées.
          </Hint>
          <Field
            label="Clé de secours"
            value={recoveryKey}
            onChange={setRecoveryKey}
            placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
          />
          {recovery.isError && <ErrorNote>{describe(recovery.error)}</ErrorNote>}
          <Button
            disabled={recoveryKey.trim() === '' || recovery.isPending}
            onClick={() => {
              recovery.mutate();
            }}
          >
            {recovery.isPending ? 'Vérification…' : 'Valider la clé'}
          </Button>
          <Button
            variant="quiet"
            onClick={() => {
              setShowRecovery(false);
              setRecoveryKey('');
            }}
          >
            Annuler
          </Button>
        </>
      )}
    </AuthShell>
  );
}
